import Application from "../../Application";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import { IOrganisation } from "../../organisations/model/Organisation";
import { ICaseDecision } from "../model/CaseDecision";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import { IUser } from "../../user/model/User";
import CreateCaseDecisionInteractor from "../interactor/CreateCaseDecisionInteractor";
import GetCaseDecisionTypeIndexDataInteractor from "../interactor/GetCaseDecisionTypeIndexDataInteractor";
import SummaryTranslation from "../../core/model/translations/SummaryTranslation";
import { TDocumentTypeIndexCategoryPresentationModel } from "../../documentTypes/presenter/DocumentTypeIndexCategoryPresentationModel";
import { IDocumentType } from "../../documentTypes/model/DocumentType";
import DocumentTypeIndexCategoryPresentationModel from "../../documentTypes/presenter/DocumentTypeIndexCategoryPresentationModel";
import GetOrganisationCaseDecisionTypesInteractor from "../../organisations/interactors/GetOrganisationCaseDecisionTypesInteractor";
import { ICourtCase } from "../../caseLawDocuments/model/CourtCase";
import { TCourtCaseSelectionModel } from "../../caseLawDocuments/presenter/CaseLawDocumentCreationPresenter";
import ShowInfoMessageInteractor from "../../core/interactor/notifications/ShowInfoMessageInteractor";
import { TSearchQueryRequest } from "../../search/service/SearchService";
import RunSimpleSearchQueryInteractor from "../../search/interactor/RunSimpleSearchQueryInteractor";
import { CourtCaseType } from "../../caseLawDocuments/common/Constants";
import { IAttachment } from "../../caseLawDocuments/model/Attachment";
import CreateAttachmentFileInteractor from "../../caseLawDocuments/interactor/attachments/CreateAttachmentFileInteractor";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import { ICourtCaseConnectionType } from "../../caseLawDocuments/model/CourtCaseConnectionType";
import { Constants } from "../../core/common/Constants";
import FindCourtCaseWithAllDecisionsInteractor from "../../caseLawDocuments/interactor/FindCourtCaseWithAllDecisionsInteractor";
import { IInvalidatedCaseDecision } from "../model/InvalidatedCaseDecision";
import { TSearchCriteriaFilter } from "../../search/model/SearchCriteria";
import { CaseDecisionStatus } from "../common/Constants";

export interface TCaseDecisionCreationPresenter extends TLoadingAwarePresenter {
  organisations: IOrganisation[];
  selectedOrganisationId: number;
  caseDecisionTypes: IDocumentType[];
  organisationIndexes: TDocumentTypeIndexCategoryPresentationModel[];
  caseDecisionTypeId?: number;
  courtCase?: ICourtCase;
  caseDecisionTypeOrganisationIndex: TDocumentTypeIndexCategoryPresentationModel[];
  user?: IUser;
  courtCaseSelectionResult?: TCourtCaseSelectionModel;
  createCourtCasePopupVisible: boolean;
  uploadedAttachment?: IAttachment;
  decisionTypes: IGenericCodebookItem[];
  courtCaseTypes: IGenericCodebookItem[];
  createCourtCaseModalVisible: boolean;
  nonExistantCourtCaseNumber?: string;
  courtCaseDetailsModalVisible?: boolean;
  courtCaseConnectionTypeList: ICourtCaseConnectionType[];
  selectedCaseDecision?: ICaseDecision;
  invalidatePublishedCaseDecisionsVisible: boolean;
  publishedCaseDecisions: any;
}
export interface ICaseDecisionCreationPresenter
  extends TCaseDecisionCreationPresenter,
    TPresentable {
  loadReferenceValues(
    organisations: IOrganisation[],
    courtCaseConnectionTypeList: ICourtCaseConnectionType[],
    decisionTypes: IGenericCodebookItem[],
    courtCaseTypes: IGenericCodebookItem[]
  ): void;
  setOrganisationId(organisationId: number): void;
  createCaseDecision(caseDecision: ICaseDecision): void;
  showCaseDecisionList(): void;
  setCaseDecisionTypeId(caseDecisionTypeId: number): void;
  findCourtCaseDetails(courtCaseNumber: string): void;
  showCreateCourtCasePopupVisible(value: boolean): void;
  showCreateCourtCaseModalVisible(value: boolean): void;
  createCourtCase(courtCase: ICourtCase): void;
  createAttachment(file: any, fileName?: string): any;
  updateCourtCaseDetailsModalVisibility(state: boolean): any;
  updateCourtCaseSelection(result: TCourtCaseSelectionModel): any;
  selectCaseDecision(decision: ICaseDecision): void;
  setInvalidatePublishedCaseDecisions(value: boolean): void;
  onSearch(value: string): void;
}
const defaultState: TCaseDecisionCreationPresenter = {
  organisations: [],
  selectedOrganisationId: NaN,
  caseDecisionTypes: [],
  organisationIndexes: [],
  caseDecisionTypeOrganisationIndex: [],
  createCourtCasePopupVisible: false,
  createCourtCaseModalVisible: false,
  courtCaseTypes: [],
  decisionTypes: [],
  courtCaseDetailsModalVisible: false,
  courtCaseConnectionTypeList: [],
  invalidatePublishedCaseDecisionsVisible: false,
  publishedCaseDecisions: {}
};
const CaseDecisionCreationPresenter = withStore<
  ICaseDecisionCreationPresenter,
  TCaseDecisionCreationPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): ICaseDecisionCreationPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TCaseDecisionCreationPresenter>();

    const loadReferenceValues = (
      organisations: IOrganisation[],
      courtCaseConnectionTypeList: ICourtCaseConnectionType[],
      decisionTypes: IGenericCodebookItem[],
      courtCaseTypes: IGenericCodebookItem[]
    ) => {
      const user = _store.getState<any>().user;
      if (user && user.isOperator()) {
        setOrganisationId(user.organisationId);
      }
      _store.update({
        organisations,
        user,
        courtCaseConnectionTypeList,
        courtCaseTypes,
        decisionTypes,
        courtCase: null,
        courtCaseDetailsModalVisible: false,
        courtCaseSelectionResult: null
      });
    };
    const getCaseDecisionTypeIndexes = async (caseDecisionTypeId: number) => {
      const { selectedLanguage, selectedOrganisationId } =
        _store.getState<any>();
      let organisationIndexes = await _application.container
        .resolve<GetCaseDecisionTypeIndexDataInteractor>(
          "getCaseDecisionTypeIndexData"
        )
        .execute(caseDecisionTypeId, selectedOrganisationId);

      _store.update({
        organisationIndexes: organisationIndexes.map((item) =>
          DocumentTypeIndexCategoryPresentationModel(item, selectedLanguage)
        )
      });
    };

    const updateCourtCaseDetailsModalVisibility = (state: boolean) => {
      _store.update({ courtCaseDetailsModalVisible: state });
    };

    const updateCourtCaseSelection = (result: TCourtCaseSelectionModel) => {
      result?.caseDecision?.organisationId &&
        setOrganisationId(result?.caseDecision?.organisationId);
      _store.update({
        courtCaseSelectionResult: result,
        courtCase: result.courtCase
      });
    };

    const createCourtCase = (courtCase: any) => {
      const { uploadedAttachment } =
        _store.getState<TCaseDecisionCreationPresenter>();
      if (!uploadedAttachment) {
        _application.container
          .resolve<ShowInfoMessageInteractor>("showInfoMessage")
          .execute(translate("editors.case_decisions.no_attachment"));
        return false;
      } else if (courtCase?.courtCaseNumber) {
        showCreateCourtCaseModalVisible(false);
        courtCase.decisions = [
          {
            name: courtCase.caseDecisionName,
            attachmentId: uploadedAttachment?.id,
            decisionDate: courtCase.decisionDate,
            decisionTypeId: courtCase.decisionTypeId,
            valid: true
          }
        ];
        courtCase.type === CourtCaseType.PRIMARY
          ? _store.update({
              courtCase: courtCase,
              courtCaseDetailsModalVisible: true,
              nonExistantCourtCaseNumber: undefined
            })
          : _store.update({
              courtCaseDetailsModalVisible: true,
              nonExistantCourtCaseNumber: undefined
            });
        return true;
      }
    };

    const showCreateCourtCaseModalVisible = (value: boolean) => {
      _store.update({ createCourtCaseModalVisible: value });
      value === false &&
        _store.update({ nonExistantCourtCaseNumber: undefined });
    };

    const createAttachment = async (file: any, fileName?: string) => {
      if (file?.status !== "removed") {
        let attachment = {
          name: fileName,
          valid: true,
          anonymised: false
        } as IAttachment;
        if (typeof file !== "string") {
          let record = await _application.container
            .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
            .execute(attachment, file);

          _store.update({
            uploadedAttachment: record
          });
          loader.stop("attachmentsLoader");
          _application.container
            .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
            .execute(translate("editors.project.create_success_message"));
          return record;
        }
      } else {
        _store.update({
          uploadedAttachment: undefined
        });
        return undefined;
      }
    };

    const getOrganisationCaseDecisionTypes = async (organisationId: number) => {
      loader.start("caseDecisionLoader");
      let caseDecisionTypes = await _application.container
        .resolve<GetOrganisationCaseDecisionTypesInteractor>(
          "getOrganisationCaseDecisionTypes"
        )
        .execute(organisationId);
      _store.update({
        caseDecisionTypes
      });
      if (caseDecisionTypes.length === 1) {
        setCaseDecisionTypeId(caseDecisionTypes[0].id);
      }
      loader.stop("caseDecisionLoader");
    };
    const setCaseDecisionTypeId = async (caseDecisionTypeId: number) => {
      await getCaseDecisionTypeIndexes(caseDecisionTypeId);
      _store.update({
        caseDecisionTypeId: caseDecisionTypeId
      });
    };

    const setOrganisationId = async (organisationId: number) => {
      _store.update({ selectedOrganisationId: organisationId });
      await getOrganisationCaseDecisionTypes(organisationId);
    };

    const createCaseDecision = async (caseDecision: any) => {
      loader.start("caseDecisionLoader");
      const {
        courtCase,
        courtCaseSelectionResult,
        selectedCaseDecision,
        uploadedAttachment,
        user
      } = _store.getState<TCaseDecisionCreationPresenter>();
      let newCaseDecision = selectedCaseDecision;
      if (!selectedCaseDecision) {
        newCaseDecision = courtCaseSelectionResult?.caseDecision;
      }
      const { selectedLanguage } = _store.getState<any>();
      let elementList =
        caseDecision.organisationIndexList &&
        caseDecision.organisationIndexList.length > 0
          ? caseDecision.organisationIndexList.flat(1)
          : [];
      let translations = caseDecision.summaryTranslations?.map((item) => {
        var element = SummaryTranslation(item);
        element.organisationId = caseDecision.organisationId;
        return element;
      });

      let originalSummary = translations?.find(
        (item) => item.languageId === selectedLanguage.id
      );
      caseDecision.extId = newCaseDecision?.extId;
      caseDecision.summary = originalSummary?.summaryTranslation ?? "";
      caseDecision.summaryTranslations = translations;
      caseDecision.courtCaseId = courtCase?.id;
      caseDecision.caseNumber = courtCase?.courtCaseNumber;
      caseDecision.decisionTypeId = newCaseDecision?.decisionTypeId;
      caseDecision.decisionDate = newCaseDecision?.decisionDate;
      caseDecision.name = newCaseDecision?.name;
      caseDecision.valid = Constants.YES;
      caseDecision.attachmentId = newCaseDecision?.attachmentId;
      if (!caseDecision.organisationId && user) {
        caseDecision.organisationId = user.organisationId;
      }
      let invalidatedCaseDecisions: IInvalidatedCaseDecision = {
        caseDecisionIdList: caseDecision.invalidatedCaseDecisions,
        invalidationReason: caseDecision.invalidationReason
      };
      let record = await _application.container
        .resolve<CreateCaseDecisionInteractor>("createCaseDecision")
        .execute(
          caseDecision,
          elementList.filter((item) => item !== null && item !== undefined),
          courtCaseSelectionResult?.courtCase,
          invalidatedCaseDecisions
        );
      if (uploadedAttachment) {
        createAttachment(uploadedAttachment?.content, uploadedAttachment?.name);
      }
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      loader.stop("caseDecisionLoader");
      showCaseDecisionDetails(record.id);
      _store.update({
        selectedCourtCases: [],
        courtCaseSelectionResult: null,
        uploadedAttachment: null
      });
      return true;
    };

    const showCaseDecisionDetails = (id: string) => {
      return _application.navigator.push({
        pathname: `/case-decisions/${id.toString()}/details`
      });
    };
    const showCaseDecisionList = () => {
      return _application.navigator.push({
        pathname: `/legal-views`
      });
    };

    const findCourtCaseDetails = async (courtCaseNumber: string) => {
      let { courtCaseSelectionResult } =
        store.getState<TCaseDecisionCreationPresenter>();
      if (!courtCaseNumber || courtCaseNumber.trim() === "") {
        _store.update({ courtCase: undefined, courtCaseSelectionResult: null });
        return false;
      }
      if (
        courtCaseSelectionResult?.courtCase?.courtCaseNumber === courtCaseNumber
      ) {
        _store.update({
          courtCase: courtCaseSelectionResult.courtCase,
          courtCaseDetailsModalVisible: true
        });
        return true;
      }
      let _searchQuery: TSearchQueryRequest = {
        ccn: courtCaseNumber
      };
      let queryResult = await _application.container
        .resolve<RunSimpleSearchQueryInteractor>("runSimpleSearchQuery")
        .execute(_searchQuery);
      var record = await _application.container
        .resolve<FindCourtCaseWithAllDecisionsInteractor>(
          "findCourtCaseWithAllDecisions"
        )
        .execute(courtCaseNumber);
      record.hasCaseLawDocs = queryResult?.results.length > 0;
      if (record.courtCaseNumber) {
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("editors.case_law_documents.primary_cases.success")
          );
        _store.update({
          courtCaseSelectionResult: null,
          courtCase: record,
          courtCaseDetailsModalVisible: true
        });
      } else {
        _application.container
          .resolve<ShowInfoMessageInteractor>("showInfoMessage")
          .execute(
            translate("editors.case_law_documents.connected_cases.not_found")
          );

        showCreateCourtCasePopupVisible(true);
        _store.update({
          courtCase: undefined,
          courtCaseSelectionResult: null,
          courtCaseDetailsModalVisible: false,
          nonExistantCourtCaseNumber: courtCaseNumber
        });
      }
      return record !== undefined ?? false;
    };

    const showCreateCourtCasePopupVisible = (value: boolean) => {
      _store.update({ createCourtCasePopupVisible: value });
    };

    const selectCaseDecision = (decision: ICaseDecision) => {
      _store.update({ selectedCaseDecision: decision });
    };

    const setInvalidatePublishedCaseDecisions = async (value: boolean) => {
      _store.update({ invalidatePublishedCaseDecisionsVisible: value });
    };

    const onSearch = async (value: string) => {
      if (value.length >= 3) {
        let query: TSearchCriteriaFilter = {
          phrase: value
        };

        let _searchQuery: TSearchQueryRequest = {
          phrase: query.phrase ?? "",
          status: CaseDecisionStatus.PUBLISHED
        };
        _store.update({
          searchQuery: _searchQuery
        });

        let result = await _application.container
          .resolve<RunSimpleSearchQueryInteractor>("runSimpleSearchQuery")
          .execute(_searchQuery);
        result &&
          _store.update({
            publishedCaseDecisions: result.formattedResults
          });
      }
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadReferenceValues,
      setOrganisationId,
      createCaseDecision,
      showCaseDecisionList,
      setCaseDecisionTypeId,
      findCourtCaseDetails,
      showCreateCourtCasePopupVisible,
      showCreateCourtCaseModalVisible,
      createCourtCase,
      createAttachment,
      updateCourtCaseDetailsModalVisibility,
      updateCourtCaseSelection,
      selectCaseDecision,
      setInvalidatePublishedCaseDecisions,
      onSearch
    };
  },
  defaultState
);
export default CaseDecisionCreationPresenter;
